"use client";

import { apiUrl } from "./basePath";
import { authHeaders } from "./auth";
import { INVITE_SESSION_KEYS, unwrap } from "./invite";
import { startCandidateSession } from "@/app/_mock/journey";

/**
 * Entering the runner from the signed-in student dashboard.
 *
 * Institute students do not receive an invite — they sign in, see their
 * assessments and pick one. The v1 dashboard hands the assignment over here,
 * and this trades their login for the same scoped session an invited candidate
 * holds. Everything past this point is identical for both, which is the point:
 * the runner has one way in, not two.
 */

/** Where to send the candidate when they are finished. Only ever a path on
 *  this origin — a full URL from the query string would make the completion
 *  screen an open redirect. */
export const RETURN_TO_KEY = "pl.v2.return_to";

export function rememberReturnTo(raw: string | null): void {
  if (!raw) return;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return;
    cached = `${url.pathname}${url.search}${url.hash}`;
    sessionStorage.setItem(RETURN_TO_KEY, cached);
  } catch {
    /* not a URL we can trust — the candidate simply gets no button */
  }
}

/* Read once and held, so the completion screen can drop the session it came in
   with — the whole point of that screen — without the button under the
   candidate's cursor disappearing as it does. */
let cached: string | null | undefined;

export function getReturnTo(): string | null {
  if (cached === undefined) {
    try {
      cached = sessionStorage.getItem(RETURN_TO_KEY);
    } catch {
      cached = null;
    }
  }
  return cached;
}

/** Drop the previous sitting's return path — including the module copy above,
 *  which `clearInviteSession` cannot reach. Called when the tab switches to a
 *  different assignment, so the completion screen never offers the last
 *  assessment's "back" link. */
export function forgetReturnTo(): void {
  cached = null;
  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* storage disabled — nothing was stored to clear */
  }
}

/**
 * Exchanges the signed-in student's token for a scoped session on one
 * assignment, and stores it exactly where an invite stores its own — so the
 * summary, the runner, saving, submitting and proctoring all carry on
 * unchanged.
 */
export async function openStudentSession(assessmentAssignedId: string): Promise<void> {
  const headers = authHeaders();
  if (!headers.Authorization) throw new Error("Sign in to open this assessment.");
  const response = await fetch(apiUrl(`/api/assessment/session/${encodeURIComponent(assessmentAssignedId)}`), {
    method: "POST",
    headers,
    cache: "no-store",
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) throw new Error(String(payload.message || "This assessment could not be opened."));
  const data = unwrap(payload);
  const token = data.token;
  const email = data.email;
  if (!token) throw new Error("This assessment could not be opened.");

  const put = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") sessionStorage.setItem(key, String(value));
  };
  put(INVITE_SESSION_KEYS.scopedJwt, token);
  put(INVITE_SESSION_KEYS.assessmentAssignedId, data.assessment_assigned_id ?? assessmentAssignedId);
  put(INVITE_SESSION_KEYS.assessmentSetId, data.assessment_set_id);
  put(INVITE_SESSION_KEYS.assessmentType, data.assessment_type);
  put(INVITE_SESSION_KEYS.name, data.assessment_name);
  put(INVITE_SESSION_KEYS.email, email);
  put(INVITE_SESSION_KEYS.allowProctoring, data.allowProctoring ?? data.allow_proctoring ?? false);
  put(INVITE_SESSION_KEYS.allowVerification, data.allowVerification ?? data.allow_verification ?? true);
  // The AI Interview runner still reads its own pair while it is migrated.
  put("ai_interview_scoped_jwt", token);
  put("ai_interview_assessment_assigned_id", data.assessment_assigned_id ?? assessmentAssignedId);
  // This is the signed-in institute-dashboard handoff — the one entry point
  // that is unambiguously institute (see journey.ts's startCandidateSession).
  if (email) startCandidateSession(String(email), "institute");
}
